import network from '@/services/network';
import { BoardFarbe, BoardSpalteDto, TodoItem } from '@/types';
import { ErrorResponse } from '@/utils';

/** Zielbeschreibung eines Drag&Drop-Zugs (Aufgaben-Board Welle 1). */
export interface KartenZug {
  /** null = Fixspalte „Zu erledigen". */
  board_column_id: number | null;
  /** true = Fixspalte „Erledigt" — setzt serverseitig `completed_at`. */
  completed: boolean;
  position: number;
}

const getColumns = async (): Promise<{ data: BoardSpalteDto[] }> => {
  try {
    const { data } = await network.get<{ data: BoardSpalteDto[] }>('/board/columns');
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const createColumn = async (nutzlast: { name: string; color: BoardFarbe }): Promise<{ data: BoardSpalteDto }> => {
  try {
    const { data } = await network.post<{ data: BoardSpalteDto }>('/board/columns', nutzlast);
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const updateColumn = async (
  id: number,
  nutzlast: { name?: string; color?: BoardFarbe },
): Promise<{ data: BoardSpalteDto }> => {
  try {
    const { data } = await network.put<{ data: BoardSpalteDto }>(`/board/columns/${id}`, nutzlast);
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/**
 * Neue Reihenfolge der eigenen Spalten (Feedback-Runde 2, Ziffer 4).
 *
 * Geschickt wird die VOLLSTAENDIGE Liste, nicht „Spalte X auf Platz 3": eine
 * Verschiebung aendert die Position mehrerer Spalten zugleich. Zurueck kommt
 * die Liste, wie der Server sie fuehrt — damit muss die Oberflaeche nicht
 * raten, ob er ihr gefolgt ist.
 */
const reorderColumns = async (ids: number[]): Promise<{ data: BoardSpalteDto[] }> => {
  try {
    const { data } = await network.put<{ data: BoardSpalteDto[] }>('/board/columns/reorder', { ids });
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const deleteColumn = async (id: number): Promise<void> => {
  try {
    await network.delete(`/board/columns/${id}`);
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/**
 * Anlegen und Bearbeiten einer Aufgabe vom Board aus (31.08.2026).
 *
 * Beide Wege liefern die Karte in derselben Form zurueck, in der das Board
 * sie ohnehin fuehrt (`TodoItem`) — serverseitig `BoardCardResource`. Damit
 * setzt der Store die Antwort direkt ein, statt das ganze Board neu zu laden;
 * beim seriellen Anlegen im Quick-Add ist das der Unterschied zwischen
 * „Feld bleibt stehen" und „Liste baut sich nach jedem Enter neu auf".
 */
export interface AufgabeAnlegen {
  title: string;
  description?: string | null;
  /** Optional — eine Aufgabe darf ohne Frist existieren (OF-1a). */
  due_date?: string | null;
  /** Traegt die Frist eine gemeinte Uhrzeit? (Feedback-Runde 2) */
  due_has_time?: boolean;
  /** Optionale Kartenfarbe aus derselben Palette wie die Spalten. */
  color?: BoardFarbe | null;
  /** Optional — eine Aufgabe darf ohne Fall existieren (OF-1a). */
  deal_id?: number | null;
  /** Zielspalte; fehlt sie, landet die Karte in „Zu erledigen". */
  board_column_id?: number | null;
}

export interface AufgabeAendern {
  title?: string;
  description?: string | null;
  due_date?: string | null;
  due_has_time?: boolean;
  /** Mitgeschicktes `null` heisst „Farbe entfernen". */
  color?: BoardFarbe | null;
  completed_at?: string | null;
  /** Mitgeschicktes `null` heisst „Fall entfernen"; Weglassen laesst ihn stehen. */
  deal_id?: number | null;
}

const createCard = async (nutzlast: AufgabeAnlegen): Promise<{ data: TodoItem }> => {
  try {
    const { data } = await network.post<{ data: TodoItem }>('/follow-ups', nutzlast);
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const updateCard = async (followUpId: number, nutzlast: AufgabeAendern): Promise<{ data: TodoItem }> => {
  try {
    const { data } = await network.put<{ data: TodoItem }>(`/follow-ups/${followUpId}`, nutzlast);
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const deleteCard = async (followUpId: number): Promise<void> => {
  try {
    await network.delete(`/follow-ups/${followUpId}`);
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const moveCard = async (followUpId: number, zug: KartenZug): Promise<{ data: unknown }> => {
  try {
    const { data } = await network.patch<{ data: unknown }>(`/follow-ups/${followUpId}/board`, zug);
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const boardApi = {
  getColumns,
  createColumn,
  updateColumn,
  reorderColumns,
  deleteColumn,
  moveCard,
  createCard,
  updateCard,
  deleteCard,
};

export default boardApi;
